import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

interface App {
  id: string;
  name: string;
  description: string;
  tag: string;
  route: string;
}

interface Category {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  apps: App[];
}

const categories: Category[] = [
  {
    id: 'rigid',
    title: 'Rigid Pavements',
    subtitle: 'Concrete pavement analysis & design',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="36">
        <rect x="2" y="16" width="36" height="8" rx="1" fill="currentColor" opacity="0.15"/>
        <rect x="2" y="16" width="36" height="8" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="2" y="26" width="36" height="6" rx="1" fill="currentColor" opacity="0.08"/>
        <rect x="2" y="26" width="36" height="6" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <line x1="14" y1="16" x2="14" y2="24" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
        <line x1="26" y1="16" x2="26" y2="24" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
        <path d="M8 10 L20 6 L32 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    apps: [
      { id: 'pittrigid', name: 'PittRigid', description: 'Structural analysis of jointed plain concrete pavements', tag: 'Analysis', route: '/pittrigid' },
      { id: 'eot-pa', name: 'Early Opening to Traffic — PA', description: 'Predict safe opening times for newly poured concrete in Pennsylvania', tag: 'Design', route: 'https://software.pavements.pitt.edu//EOP-PA' },
    ],
  },
  {
    id: 'flexible',
    title: 'Flexible Pavements',
    subtitle: 'Asphalt pavement analysis & design',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="36">
        <path d="M2 14 Q20 10 38 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <path d="M2 14 Q20 10 38 14 L38 22 Q20 18 2 22 Z" fill="currentColor" opacity="0.15"/>
        <path d="M2 14 Q20 10 38 14 L38 22 Q20 18 2 22 Z" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M2 22 L38 22 L38 30 L2 30 Z" fill="currentColor" opacity="0.08" stroke="currentColor" strokeWidth="1.8"/>
        <circle cx="20" cy="8" r="2.5" fill="currentColor" opacity="0.5"/>
      </svg>
    ),
    apps: [
      { id: 'mnlayer', name: 'MnLayer', description: 'Multi-layer elastic analysis for flexible pavement systems', tag: 'Analysis', route: '/mnlayer' },
    ],
  },
];

const AppPill: React.FC<{ app: App }> = ({ app }) => {
  const navigate = useNavigate();

 const handleClick = () => {
  if (app.route.startsWith('http')) {
    window.open(app.route, '_blank');
  } else {
    window.scrollTo(0, 0);
    navigate(app.route);
  }
};

  return (
    <button
      onClick={handleClick}
      className="group w-full text-left p-3 rounded-xl bg-gray-50 border border-transparent hover:bg-blue-50 hover:border-blue-200 hover:translate-x-1 transition-all duration-200"
      style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gridTemplateRows: 'auto auto', columnGap: '0.75rem', alignItems: 'start' }}
    >
      <span className="row-span-1 self-center text-[10px] font-mono font-medium tracking-widest uppercase bg-[#FFB81C] text-[#001f5b] px-2 py-0.5 rounded">
        {app.tag}
      </span>
      <span className="font-semibold text-sm text-[#001f5b] leading-snug" style={{ gridColumn: 2, gridRow: 1 }}>
        {app.name}
      </span>
      <span className="text-xs text-gray-500 leading-snug mt-0.5" style={{ gridColumn: 2, gridRow: 2 }}>
        {app.description}
      </span>
      <span className="self-center text-[#003594] opacity-30 group-hover:opacity-80 group-hover:translate-x-1 transition-all duration-200 text-base" style={{ gridColumn: 3, gridRow: '1 / 3' }}>
        →
      </span>
    </button>
  );
};

const CategoryCard: React.FC<{ category: Category }> = ({ category }) => (
  <div className="group bg-white rounded-2xl border border-gray-200 p-7 shadow-[0_4px_24px_rgba(0,53,148,0.08)] hover:shadow-[0_12px_40px_rgba(0,53,148,0.15)] hover:-translate-y-1 transition-all duration-200">
    <div className="flex items-start gap-4 pb-5 mb-5 border-b border-gray-100">
      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-[#003594] flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-200">
        {category.icon}
      </div>
      <div>
        <h2 style={{ fontFamily: "'Crimson Pro', Georgia, serif" }} className="font-semibold text-xl text-[#001f5b] leading-tight mb-0.5">
          {category.title}
        </h2>
        <p style={{ fontFamily: "'DM Mono', monospace" }} className="text-xs text-gray-400 tracking-wide">
          {category.subtitle}
        </p>
      </div>
    </div>
    <div className="flex flex-col gap-2">
      {category.apps.map(app => (
        <AppPill key={app.id} app={app} />
      ))}
    </div>
  </div>
);

const HomePage: React.FC = () => (
  <div className="min-h-screen bg-[#f7f6f2]" style={{ fontFamily: "'Outfit', sans-serif" }}>
    <Navbar />

    {/* Hero */}
    <header className="bg-[#001f5b] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 60%, rgba(255,184,28,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(0,53,148,0.4) 0%, transparent 55%)' }} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 md:py-28">
        <p style={{ fontFamily: "'DM Mono', monospace" }} className="text-[#FFB81C] text-[0.7rem] tracking-[0.16em] uppercase mb-5 opacity-90">
          University of Pittsburgh · Civil &amp; Environmental Engineering
        </p>
        <h1 style={{ fontFamily: "'Crimson Pro', Georgia, serif", fontSize: 'clamp(2.6rem, 6vw, 3.2rem)', fontWeight: 300, lineHeight: 1.05 }} className="text-white mb-6">
          Pavement<br />
          <em className="italic text-[#FFB81C]">Engineering</em><br />
          App Hub
        </h1>
        <p className="text-white/60 text-lg leading-relaxed max-w-md mb-9">
          A collection of research-grade tools for the analysis and design of rigid and flexible pavement systems. Built for engineers, researchers, and students.
        </p>
        <a href="#apps" className="inline-flex items-center gap-2 px-7 py-3 rounded bg-[#FFB81C] text-[#001f5b] font-semibold text-[0.95rem] shadow-[0_4px_20px_rgba(255,184,28,0.3)] hover:bg-[#ffd06a] hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(255,184,28,0.4)] transition-all duration-200">
          Explore Tools
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10 9.293 5.707A1 1 0 0110 3z" clipRule="evenodd"/>
          </svg>
        </a>
      </div>
    </header>

    {/* Apps */}
    <main id="apps" className="py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <h2 style={{ fontFamily: "'Crimson Pro', Georgia, serif" }} className="font-semibold text-[1.9rem] text-[#001f5b] mb-1">
            Available Tools
          </h2>
          <p style={{ fontFamily: "'DM Mono', monospace" }} className="text-sm text-gray-400 tracking-wide">
            Select a tool to begin your analysis
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map(cat => (
            <CategoryCard key={cat.id} category={cat} />
          ))}
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

export default HomePage;